<template>
	<div class="level-up-popup">
		<div
			ref="animation2"
			class="level-up-popup__header animated-element--faded"
		>
			{{ translate("ui.new_level") }}
		</div>
		<div
			class="level-shield level-shield__animation-init animated-element--faded"
			ref="animation1"
		>
			<div
				ref="animation1b"
				class="level-shield__shine animated-element--faded"
			/>
			<div class="level-shield__img">
				<div
					class="level-shield__lvl animated-element--faded"
					ref="animation1a"
				>
					<div class="level-shield__text level-shield__text-shadow">
						{{ mainLevel }}
					</div>
					<div class="level-shield__text">
						{{ mainLevel }}
					</div>
					<div class="level-shield__text-glare">
						{{ mainLevel }}
					</div>
				</div>
			</div>
		</div>
		<div ref="animation3" class="animated-element--faded">
			<div
				class="level-up-popup__content-wrapper"
				:class="{
					'level-up-popup__content-wrapper--fit':
						contentHeight ===
						parseFloat(dynamicContentStyle['height']),
				}"
				:style="[dynamicContentStyle]"
			>
				<ScrollFade ref="scroll-fade">
					<template #scrollContent>
						<div
							class="level-up-popup__content"
							ref="contentElement"
						>
							<div
								class="level-up-popup__content-box"
								v-if="computedBuildings.length > 0"
							>
								<div
									class="subsection-header animated-element--faded"
								>
									<RaggedBoxBackground
										slug="subsection-bg"
										:skin="{
											stopColor1: '#d7bd8d',
											stopColor2: '#d7bd8d',
										}"
									/>
									<div class="subsection-text">
										<span class="subsection-text__text">
											{{
												translate(
													"ui.unlocked_buildings",
												)
											}}
										</span>
										<AppNotification
											:value="computedBuildings.length"
										/>
									</div>
								</div>
								<TileUpgradeBox
									class="animated-element--faded"
									v-for="(entity, index) in computedBuildings"
									:key="index"
									:heading="entity.name"
									:description="
										switchParagraphFormat(
											entity.description,
										)
									"
									:type="TileUpgradeBoxTypes.LIST"
									:consumption="entity?.consumption ?? null"
									:entity="entity"
									:icon-name="
										getTileIconName(entity.frameName)
									"
									:context="UIContexts.LEVEL_UP"
									:skin="
										tilerepoPremiumBuildings.has(
											entity.frameName,
										)
											? TileUpgradeBoxSkinTypes.BUILDING_PREMIUM
											: TileUpgradeBoxSkinTypes.BUILDING
									"
									:isPromotion="true"
								/>
							</div>
							<div
								v-if="computedUpgrades.length > 0"
								class="level-up-popup__content-box"
							>
								<div
									class="subsection-header animated-element--faded"
								>
									<RaggedBoxBackground
										slug="subsection-bg"
										:skin="{
											stopColor1: '#d7bd8d',
											stopColor2: '#d7bd8d',
										}"
									/>
									<div class="subsection-text">
										<span class="subsection-text__text">
											{{
												translate(
													"ui.unlocked_upgrades",
												)
											}}
										</span>
										<AppNotification
											:value="computedUpgrades.length"
										/>
									</div>
								</div>
								<UpgradeBox
									class="animated-element--faded"
									v-for="(upgrade, index) in computedUpgrades"
									:key="index"
									@click="toUpgrades"
									:has-redirect="true"
									:disable-click="!clickable"
									:skin="SkinTypes.LIGHT"
									:id="upgrade[0]"
									:heading="upgrade[1]?.heading"
									:description="upgrade[1]?.description"
									:descriptionReplace="
										upgrade[1]?.descriptionReplace
									"
								/>
							</div>
						</div>
					</template>
				</ScrollFade>
			</div>
		</div>
		<div ref="animation4" class="animated-element--faded">
			<AppTextButton
				class="close-btn"
				:text="translate('button.continue')"
				:type="StatusTypes.SUCCESS"
				@click="close"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppNotification from "@/components/AppNotification.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import TileUpgradeBox from "@/components/TileUpgradeBox.vue";
import UpgradeBox from "@/components/Upgrades/UpgradeBox.vue";
import { SAFE_COORDINATE_OUTSIDE_MAP } from "@/config/constants";
import {
	type FrameName,
	tilerepoDuplicateFrameNames,
	tilerepoPremiumBuildings,
} from "@/game/entities/config";
import { getNewBuildingsFromLevel } from "@/game/entities/config/buildings";
import { getNewRoadsFromLevel } from "@/game/entities/config/roads";
import { getNewTerrainsFromLevel } from "@/game/entities/config/terrain";
import {
	getNewUpgradesFromLevel,
	type UpgradeId,
	type UpgradeItem,
} from "@/game/entities/config/upgrades";
import type TileEntity from "@/game/entities/TileEntity";
import { levelManager } from "@/game/LevelManager";
import SoundService from "@/game/SoundService";
import useSocketService from "@/hooks/useSocketService";
import useTileRepository from "@/hooks/useTileRepository";
import { EmitStores } from "@/libs/SocketService";
import useSettlementsStore from "@/store/useSettlementsStore";
import {
	SkinTypes,
	StatusTypes,
	TileUpgradeBoxSkinTypes,
	TileUpgradeBoxTypes,
	UIContexts,
} from "@/types/ui";
import {
	animateElement,
	resetAnimateElement,
	resetAnimation,
	scrollStepwiseDownIfNeeded,
	skipAnimationOnClick,
} from "@/utils/animateElement";
import { switchParagraphFormat } from "@/utils/switchParagraphFormat";
import { getTileIconName } from "@/utils/tileIconName";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import type { ShallowRef } from "vue";
import {
	computed,
	markRaw,
	onMounted,
	onUnmounted,
	ref,
	useTemplateRef,
} from "vue";

const emit = defineEmits(["closePopup"]);

const settlementStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementStore);

const tilerepo = markRaw(useTileRepository());
const soundService = markRaw(SoundService.getInstance());
const socketService = markRaw(useSocketService());

const contentElement = useTemplateRef<HTMLDivElement>("contentElement");
const scrollFade = useTemplateRef<InstanceType<typeof ScrollFade> | null>(
	"scroll-fade",
);

const computedBuildings = computed((): Array<TileEntity> => {
	const buildings = getNewBuildingsFromLevel(mainLevel.value);
	const terrains = getNewTerrainsFromLevel(mainLevel.value);
	const roads = getNewRoadsFromLevel(mainLevel.value);
	const all = [...buildings, ...terrains, ...roads];

	return all
		.filter(
			(
				frameName: FrameName,
				index: number,
				self: Array<FrameName>,
			): boolean => self.indexOf(frameName) === index,
		)
		.map((frameName: FrameName): TileEntity | null => {
			const entity = tilerepo.createEntityByFrameName(frameName, {
				x: SAFE_COORDINATE_OUTSIDE_MAP,
				y: SAFE_COORDINATE_OUTSIDE_MAP,
			});
			if (!entity) return null;
			entity.showProductionInfo = true;
			return entity;
		})
		.filter((entity: TileEntity | null): entity is TileEntity => {
			return (
				entity !== null &&
				!tilerepoDuplicateFrameNames.has(entity.frameName)
			);
		})
		.sort(
			(
				{ frameName: aFrameName }: TileEntity,
				{ frameName: bFrameName }: TileEntity,
			): number =>
				buildings.indexOf(aFrameName) - buildings.indexOf(bFrameName),
		);
});

const computedUpgrades = computed((): Array<[UpgradeId, UpgradeItem]> => {
	return Array.from(
		getNewUpgradesFromLevel(mainLevel.value).entries(),
	) as Array<[UpgradeId, UpgradeItem]>;
});

const clickable = ref<boolean>(false);
const isAnimationSkipped = ref<boolean>(false);

const close = (): void => {
	if (!clickable.value) return;

	soundService.playGenerickClick();
	clickable.value = false;
	resetAnimationScene();
	window.removeEventListener("click", handleSkipAnimationClick);
	emit("closePopup"); // Tu preberá kontrolu PopupStore!

	levelManager.onLevelUpFinish();
};

const toUpgrades = (): void => {
	if (!clickable.value) return;
	close();
};

const refreshPacksOnLevelUp = async (): Promise<void> => {
	try {
		await socketService.emitSaveRequest({
			stores: [EmitStores.SETTLEMENTS, EmitStores.USER],
		});
		socketService.emitLoadRequest({
			stores: [EmitStores.PACKS],
		});
	} catch (error) {
		console.error("Nepodarilo sa aktualizovať packy po level-upe:", error);
	}
};

const contentHeight = computed((): number => {
	return contentElement.value?.getBoundingClientRect().height ?? 0;
});

const dynamicContentStyle = computed((): Record<string, string> => {
	const maxHeight = 312;
	return {
		height:
			contentHeight.value > maxHeight
				? `${maxHeight}px`
				: `${contentHeight.value}px`,
	};
});

const refs: Record<string, Readonly<ShallowRef<HTMLDivElement | null>>> = {
	animation1: useTemplateRef<HTMLDivElement>("animation1"),
	animation1a: useTemplateRef<HTMLDivElement>("animation1a"),
	animation1b: useTemplateRef<HTMLDivElement>("animation1b"),
	animation2: useTemplateRef<HTMLDivElement>("animation2"),
	animation3: useTemplateRef<HTMLDivElement>("animation3"),
	animation4: useTemplateRef<HTMLDivElement>("animation4"),
};

const animateScene = (): void => {
	const animation0 = window.document.querySelector(".popup-wrapper");
	animation0?.classList.add("animated-element--faded");

	animateElement(
		animation0 as HTMLElement,
		"fade-in",
		"1s",
		50,
		"cubic-bezier(.26,.02,.22,1)",
	);
	animateElement(
		refs.animation1.value as HTMLElement,
		"bounce-in-xl",
		"0.8s",
		640,
		"cubic-bezier(.26,.02,.22,1)",
	);
	animateElement(
		refs.animation1a.value as HTMLElement,
		"zoom-in-out-low",
		"0.48s",
		1200,
		"cubic-bezier(.26,.02,.22,1)",
	);

	setTimeout((): void => {
		refs.animation1a.value
			?.querySelector(".level-shield__text-glare")
			?.classList.add("animated-element--glared-text-trbl");
	}, 1040);

	setTimeout((): void => {
		refs.animation1.value?.classList.remove("level-shield__animation-init");
	}, 2000);

	animateElement(
		refs.animation1b.value as HTMLElement,
		"spinner",
		"8s",
		2320,
		"linear",
		"infinite",
	);

	setTimeout((): void => {
		refs.animation1b.value?.classList.remove("animated-element--faded");
	}, 2320);

	animateElement(
		refs.animation2.value as HTMLElement,
		"fade-in",
		"0.8s",
		2720,
		"cubic-bezier(.26,.02,.22,1)",
	);
	animateElement(
		refs.animation3.value as HTMLElement,
		"fade-in, expand-down",
		"0.8s",
		3120,
		"cubic-bezier(.26,.02,.22,1)",
	);

	const animatedElements: Array<HTMLElement> | undefined = [
		...((refs.animation3.value?.querySelectorAll(
			".level-up-popup__content-box > div",
		) ?? []) as Array<HTMLElement>),
	];

	const container =
		scrollFade.value?.fadeContainer?.querySelector(".scroll-fade") ?? null;
	const offsetTop = 50;
	const offsetBottom = 50;

	animatedElements?.forEach((element: HTMLElement, index: number): void => {
		const delay = 3200 + index * 1300;
		const elementClassList = element.classList;

		if (elementClassList.contains("subsection-header")) {
			animateElement(element, "fade-in", "0.8s", delay);
		}

		if (elementClassList.contains("upgrade-box")) {
			animateElement(
				element,
				"fade-in-down, border-glow-once-s",
				"1s",
				delay,
			);
		}

		window.setTimeout((): void => {
			if (!container || !element.isConnected) return;
			requestAnimationFrame((): void => {
				scrollStepwiseDownIfNeeded(
					container as HTMLElement,
					element,
					offsetTop,
					offsetBottom,
				);
				if (elementClassList.contains("upgrade-box")) {
					setTimeout((): void => {
						element.classList.add(
							"animated-element--glared-element",
						);
					}, 10);
				}
			});
		}, delay);
	});

	animateElement(
		refs.animation4.value as HTMLElement,
		"bounce-in",
		"0.8s",
		3840,
		"cubic-bezier(.26,.02,.22,1)",
	);
};

const handleSkipAnimationClick = (e: MouseEvent): void => {
	isAnimationSkipped.value = true;
	refs.animation1.value?.style.setProperty("transition", "none");
	refs.animation1.value?.classList.remove("level-shield__animation-init");

	skipAnimationOnClick(
		e,
		{
			animation1: refs.animation1,
			animation1a: refs.animation1a,
			animation2: refs.animation2,
			animation3: refs.animation3,
			animation4: refs.animation4,
		},
		isAnimationSkipped.value,
		[],
	);

	const animatedElements: Array<HTMLElement> | undefined = [
		...((refs.animation3.value?.querySelectorAll(
			".level-up-popup__content-box > div",
		) ?? []) as Array<HTMLElement>),
	];
	animatedElements?.forEach((element: HTMLElement): void => {
		element.classList.remove("animated-element--faded");
		resetAnimateElement(element);
	});
};

const resetAnimationScene = (): void => {
	refs.animation1.value?.classList.add("level-shield__animation-init");
	resetAnimation({
		animation1: refs.animation1,
		animation1a: refs.animation1a,
		animation2: refs.animation2,
		animation3: refs.animation3,
		animation4: refs.animation4,
	});

	const animatedElements: Array<HTMLElement> | undefined = [
		...((refs.animation3.value?.querySelectorAll(
			".level-up-popup__content-box > div",
		) ?? []) as Array<HTMLElement>),
	];
	animatedElements?.forEach((element: HTMLElement): void => {
		element.classList.add("animated-element--faded");
		resetAnimateElement(element);
	});
};

onMounted((): void => {
	soundService.playAchievement();
	levelManager.levelUpSetup();

	setTimeout((): void => {
		clickable.value = true;
	}, 750);

	refreshPacksOnLevelUp();

	requestAnimationFrame((): void => {
		animateScene();
		window.addEventListener("click", handleSkipAnimationClick);
	});
});

onUnmounted((): void => {
	window.removeEventListener("click", handleSkipAnimationClick);
});
</script>

<style lang="scss" scoped>
.level-up-popup {
	position: relative;
	inset: 0;
	pointer-events: auto;
	@include flex-col-start;
	z-index: 2;
	min-height: 25rem; // Important for animation — initial centered position of the level shield (iOS crop fix)
	overflow: hidden;

	&__header {
		@include flex-row-center;
		padding-bottom: 0.25rem;
		width: 25rem;
		height: 2.25rem;
		color: rgb(244, 210, 120);
		font-weight: 800;
		font-size: 1.25rem;
		text-shadow: 0.04375rem 0.04375rem 0.0625rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
		@include background(
			url($path-images + "popups/level-header-bg.avif?v2"),
			contain,
			center,
			no-repeat
		);
	}

	.level-shield {
		position: relative;
		transition: margin 1s ease-out; // important for animation
		margin: 0.875rem 0 2.75rem 0; // important for animation
		width: 100%;
		height: 16.25rem;

		&__animation-init {
			margin-bottom: calc(-50% - 8.125rem);
		}

		div {
			position: absolute;
			bottom: 0;
			width: 100%;
		}

		&__shine {
			z-index: -1;
			margin: auto;
			inset: 0;
			@include background(
				url($path-images + "popups/shine.avif"),
				contain,
				center,
				no-repeat
			);
			transition: all 1s ease-out; // Required for fade-in transition when removing the --faded class
			width: 26.875rem;
			height: 26.875rem;
		}

		&__img {
			inset: 0;
			@include background(
				url($path-images + "popups/level-shield.avif"),
				contain,
				center,
				no-repeat
			);
			height: 16.25rem;
		}

		&__lvl {
			width: 100%;
			height: 100%;
		}

		&__text {
			background: linear-gradient(
				0deg,
				rgba(195, 151, 69, 1) 0%,
				rgba(255, 236, 171, 1) 100%
			);
			background-clip: text;
			height: 100%;
			font-weight: 800;
			font-size: 5.5rem;
			-webkit-text-fill-color: transparent;
			@include flex-row-center;
			width: 100%;

			&-shadow {
				background: none;
				text-shadow: 0.175rem 0.175rem 0 rgba(0, 0, 0, 0.8);
			}

			&-glare {
				width: 100%;
				height: 100%;
				font-weight: 800;
				font-size: 5.5rem;
				@include flex-row-center;
				background-clip: text;
				color: transparent;
			}
		}
	}

	&__content {
		@include flex-col-center;
		gap: 0.5rem;
		padding: 0.625rem;
		width: 100%;
		height: 100%;

		// defines dynamic height for fade scroll boundary
		&-wrapper {
			position: relative;
			box-shadow: inset 0 0 0 0.125rem rgba(255, 255, 255, 0.1);
			border-radius: 0.15rem;
			padding: 0.125rem; // inset
			@include background(
				url($path-images + "backgrounds/textures/window-texture-bg.avif"),
				90% auto,
				center,
				repeat
			);
			width: 100%;
			overflow: hidden; // IMPORTANT: iOS scrollbar overflow fix

			&::before {
				position: absolute;
				top: 0.125rem;
				left: 0.125rem;
				z-index: -1;
				border-radius: 0.15rem 0.15rem 0 0;
				width: calc(100% - 0.25rem);
				height: 100%;
				pointer-events: none;
				content: "";
			}

			&--scrollbar-scale {
				:deep(.scrollbar__thumbPlaceholder--horizontal) {
					opacity: 0 !important;
				}
			}

			&--fit {
				:deep(.scrollbar__thumbPlaceholder--vertical) {
					opacity: 0 !important;
				}

				:deep(.scrollbar__thumbPlaceholder--horizontal) {
					opacity: 0 !important;
				}
			}
		}

		&-box {
			width: 100%;
			@include flex-col-center;
			gap: 0.5rem;

			:deep(.upgrade-box__description-effect) {
				color: #13cedf;
			}
		}

		.subsection-header {
			position: relative;
			width: 100%;
			height: 2.5rem;

			:deep(.ragged-box-bg) {
				position: absolute;
				box-shadow: 0 0 0.75rem 0 rgba(49, 37, 22, 0.6);
				width: 100%;
				height: 100%;
			}

			.subsection-text {
				position: relative;
				padding: 0 0.75rem;
				width: 100%;
				height: 100%;
				@include flex-row-between;

				&__text {
					padding-bottom: 0.25rem;
					color: rgb(55, 38, 21);
					font-weight: 800;
					font-size: 1.0625rem;
					text-transform: uppercase;
				}
			}
		}
	}
	.close-btn {
		margin-top: 1.565rem;
	}
}
</style>
